// Shared validation + rate rules for the appointment create/edit flows.
// Extracted verbatim from AppointmentModal.js so the modal and the appointment
// wizard enforce identical rules and produce identical Hebrew error messages.

import { RECURRING_FREQUENCY } from "../firebase";

// Rate stepper increments. Clinic rates are almost always round numbers
// (250 / 275 / 300), so the arrows move in 25₪ jumps. An amount that is *not*
// a multiple of 5 was deliberately fine-tuned by the therapist, so from there
// the arrows move by 1 and never destroy the exact figure.
export const RATE_STEP_COARSE = 25;
export const RATE_STEP_FINE = 1;

export const getRateStep = (rawValue) => {
  const num = parseFloat(rawValue);
  if (!Number.isFinite(num)) return RATE_STEP_COARSE; // empty field → 0 → 25
  return num % 5 === 0 ? RATE_STEP_COARSE : RATE_STEP_FINE;
};

// "HH:MM" → minutes since midnight
export const timeToMinutes = (time) => {
  if (!time || !time.includes(":")) return null;
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Rate priority chain: patient.treatmentRate > treatmentType.rate >
// clinic.defaultHourlyRate. Returns { amount: string, source } where source is
// "patient" | "treatmentType" | "clinic" | null — same values rateSource uses.
export const resolveRate = (patient, treatmentType, clinicProfile) => {
  const defaultRate = clinicProfile?.defaultHourlyRate || "";
  let amount = defaultRate ? defaultRate.toString() : "";
  let source = defaultRate ? "clinic" : null;
  if (treatmentType?.rate != null) {
    amount = treatmentType.rate.toString();
    source = "treatmentType";
  }
  if (patient?.treatmentRate) {
    amount = patient.treatmentRate.toString();
    source = "patient";
  }
  return { amount, source };
};

// Duration priority chain: patient.treatmentDuration > treatmentType.duration >
// clinic.defaultMeetingTime (45 when unset). Returns { duration: number, source }
// where source is "patient" | "treatmentType" | "clinic" — mirrors resolveRate.
export const resolveDuration = (patient, treatmentType, clinicProfile) => {
  let duration = clinicProfile?.defaultMeetingTime || 45;
  let source = "clinic";
  const typeDuration = Number(treatmentType?.duration);
  if (Number.isFinite(typeDuration) && typeDuration > 0) {
    duration = typeDuration;
    source = "treatmentType";
  }
  if (patient?.treatmentDuration) {
    duration = patient.treatmentDuration;
    source = "patient";
  }
  return { duration, source };
};

// True when the picked date+time already passed (naive local wall clock —
// the whole product runs Asia/Jerusalem).
export const isPastDateTimeValue = (date, startTime, now = new Date()) => {
  if (!date || !startTime) return false;
  return new Date(`${date}T${startTime}`) < now;
};

// ---- Per-step validators. Each returns a {field: hebrewMessage} map. ----
// Merging all of them reproduces AppointmentModal's validate() exactly.

export const validatePatient = (formData) => {
  const errors = {};
  // Blocked time (חסימת זמן) — no patient to validate
  if (formData.isBlockedTime) return errors;
  if (formData.isTempCustomer) {
    if (!formData.patientName.trim()) errors.tempCustomerName = "נא להזין שם לקוח";
    if (!/^\+972\d{9}$/.test(formData.tempCustomerPhone || ""))
      errors.tempCustomerPhone = "נא להזין מספר נייד תקין";
  } else if (!formData.patientId) {
    errors.patientId = "נא לבחור מטופל";
  }
  return errors;
};

export const validateSchedule = (formData, { isEditing = false } = {}) => {
  const errors = {};
  if (!formData.date) errors.date = "נא לבחור תאריך";
  if (!formData.startTime) errors.startTime = "נא לבחור שעה";
  if (!formData.duration || formData.duration < 1) errors.duration = "משך מינימלי דקה אחת";
  if (formData.isRecurring && !isEditing) {
    const total = parseInt(formData.seriesTotalCount, 10);
    const startIdx = parseInt(formData.seriesStartIndex, 10);
    if (!total || total < 2) errors.seriesTotalCount = "מינימום 2 מפגשים בסדרה";
    if (total > 100) errors.seriesTotalCount = "מקסימום 100 מפגשים בסדרה";
    if (!startIdx || startIdx < 1) errors.seriesStartIndex = "מספר מפגש לא תקין";
    if (startIdx > total) errors.seriesStartIndex = "מספר מפגש גבוה ממספר המפגשים בסדרה";

    if (formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY) {
      if (formData.recurringSecondDay === "" || !formData.recurringSecondTime) {
        errors.recurringSecondDay = "נא לבחור יום ושעה למפגש השני";
      } else if (formData.date && formData.startTime) {
        const baseDay = new Date(`${formData.date}T00:00:00`).getDay();
        const secondDay = Number(formData.recurringSecondDay);
        const baseMinutes = timeToMinutes(formData.startTime);
        const secondMinutes = timeToMinutes(formData.recurringSecondTime);

        // Two meetings on the same weekday are fine — overlapping ones are not
        if (
          baseDay === secondDay &&
          baseMinutes !== null &&
          secondMinutes !== null &&
          Math.abs(secondMinutes - baseMinutes) < (parseInt(formData.duration, 10) || 45)
        ) {
          errors.recurringSecondDay =
            secondMinutes === baseMinutes
              ? "המפגש השני זהה למפגש הראשון"
              : "המפגש השני חופף למפגש הראשון";
        }
      }
    }
  }
  return errors;
};

// Blocked-time (חסימת זמן) single-screen validation. Cause/comments are
// optional; when a repeat is chosen the until-date is mandatory, must be after
// the start date, and is capped at one year ahead.
export const validateBlock = (formData) => {
  const errors = {};
  if (!formData.date) errors.date = "נא לבחור תאריך";
  if (!formData.startTime) errors.startTime = "נא לבחור שעה";
  const dur = parseInt(formData.duration, 10);
  if (!dur || dur < 5) errors.duration = "משך מינימלי 5 דקות";

  if (formData.blockRepeat && formData.blockRepeat !== "none") {
    if (!formData.blockUntilDate) {
      errors.blockUntilDate = "נא לבחור עד מתי לחזור על החסימה";
    } else if (formData.date) {
      const start = new Date(`${formData.date}T00:00:00`);
      const until = new Date(`${formData.blockUntilDate}T00:00:00`);
      const oneYearAhead = new Date(start);
      oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1);
      if (isNaN(until.getTime()) || until <= start) {
        errors.blockUntilDate = "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
      } else if (until > oneYearAhead) {
        errors.blockUntilDate = "ניתן לחזור על חסימה עד שנה קדימה";
      }
    }
  }
  return errors;
};

export const validateDetails = (formData) => {
  const errors = {};
  // A negative or non-numeric rate writes through to Firestore and then never
  // produces a financial transaction — reject it outright. An empty rate or 0
  // stays valid here; the missing-rate confirmation handles those.
  if (formData.paymentAmount !== "" && formData.paymentAmount != null) {
    const amt = parseFloat(formData.paymentAmount);
    if (Number.isNaN(amt) || amt < 0) errors.paymentAmount = "תעריף לא תקין";
  }
  return errors;
};

// Async-check context errors (recurring conflicts + room). `hasBlockingConflicts`
// is `recurringConflicts.length > 0 && !canSubmitDespiteConflicts()` in the caller.
export const validateExternalConflicts = ({
  hasBlockingConflicts,
  roomsEnabled,
  roomId,
  roomConflicts = [],
  roomAvailabilityError = null,
}) => {
  const errors = {};
  // Conflicts block the submit until the therapist either changes the dates
  // or explicitly confirms the double-booking from the conflict panel.
  // Series-internal collisions (selfConflict) can never be confirmed away.
  if (hasBlockingConflicts) {
    errors.conflicts = "יש התנגשויות עם פגישות קיימות";
  }
  if (roomsEnabled && roomId && roomConflicts.length > 0) errors.roomConflict = "החדר תפוס בזמן הזה";
  if (roomsEnabled && roomId && roomAvailabilityError) errors.roomAvailability = roomAvailabilityError;
  return errors;
};
